// external-links · Fase 2 del inventario de enlaces (mig 072 · página admin "Enlaces").
// ─────────────────────────────────────────────────────────────────────────
// Provee el mapa {link_key: url} de enlaces externos ACTIVOS (GET /api/links)
// vía contexto, para que los CTAs del campus (ventas/checkout/soporte/tienda)
// tomen la URL GESTIONADA desde el panel admin. SIEMPRE con fallback al valor
// hardcodeado: si la API falla o el key no existe, se usa el fallback → cero
// regresión respecto al comportamiento previo.
//
// Uso en un componente:
//   const ext = useExtLink();
//   const href = ext('pro_especial', OFFER_LINKS.pro.especial);
//
// El provider (ExternalLinksProvider) se monta una vez en CampusShell y hace
// un único fetch al cargar la sesión.

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchPublicLinks } from './api';
import type { AuthIdentity } from './types';

type LinkMap = Record<string, string>;

const ExternalLinksContext = createContext<LinkMap>({});

export function ExternalLinksProvider({
  auth,
  children,
}: {
  auth: AuthIdentity | null;
  children: ReactNode;
}): JSX.Element {
  const [map, setMap] = useState<LinkMap>({});

  useEffect(() => {
    if (!auth) return;
    let active = true;
    fetchPublicLinks(auth)
      .then((d) => {
        if (active && d.links) setMap(d.links);
      })
      .catch(() => {
        /* silencioso · si falla, los consumidores usan su fallback hardcodeado */
      });
    return () => {
      active = false;
    };
  }, [auth]);

  return <ExternalLinksContext.Provider value={map}>{children}</ExternalLinksContext.Provider>;
}

/**
 * Devuelve un resolutor `(key, fallback) => url`. Si el enlace gestionado existe
 * (y es no vacío) se usa; si no, el fallback. Reactivo: cuando el mapa carga,
 * los componentes que usan el resolutor se re-renderizan.
 */
export function useExtLink(): (key: string, fallback: string) => string {
  const map = useContext(ExternalLinksContext);
  return (key: string, fallback: string): string => {
    const v = map[key]?.trim();
    return v && isSafeUrl(v) ? v : fallback;
  };
}

// Defensa en profundidad (review seguridad 2026-06-17): los enlaces los edita
// un admin/owner y el backend ya exige https://; aun así validamos aquí antes de
// devolver una URL que acabará en un `href` o en un markdown `[label](url)`.
// Rechazamos esquemas peligrosos (javascript:, data:…) → solo http(s)/mailto/tel,
// y caracteres que romperían el markdown o permitirían inyección (espacios/
// saltos, paréntesis, `< >`, comillas, backtick, backslash). Si no es segura, se
// usa el fallback hardcodeado.
const UNSAFE_CHARS = /[\s()<>"'`\\]/;

function isSafeUrl(s: string): boolean {
  if (UNSAFE_CHARS.test(s)) return false;
  try {
    const u = new URL(s, window.location.origin);
    return ['https:', 'http:', 'mailto:', 'tel:'].includes(u.protocol);
  } catch {
    return false;
  }
}
